import React, { Fragment, useReducer, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";

import ai from "../../helpers/axios";

import RegionAutocomplete from "./region-autocomplete";
import AlternateAirportMultiSelect from "./alternate-airport-multiselect";
import FOWRegionAutocomplete from "../regions/region-autocomplete";

const reducer = (state, action) => {
  switch (action.type) {
    case "search": {
      return {
        ...state,
        query: action.query,
      };
    }
    case "fieldChanged": {
      return {
        ...state,
        airport: {
          ...state.airport,
          [action.field]: action.value,
        },
      };
    }
    case "regionsChanged": {
      return {
        ...state,
        airport: {
          ...state.airport,
          regions: action.value,
        },
      };
    }
    case "alternateAirports25Changed": {
      return {
        ...state,
        airport: {
          ...state.airport,
          alternateAirports25Mile: action.value,
        },
      };
    }
    case "alternateAirports50Changed": {
      return {
        ...state,
        airport: {
          ...state.airport,
          alternateAirports50Mile: action.value,
        },
      };
    }
    default:
      break;
  }
  return state;
};

const NewAirportContent = () => {
  const history = useHistory();

  const initialState = {
    airport: {
      code: "",
      airportName: "",
      airportActualName: "",
      city: "",
      lat: "",
      lon: "",
      municipality: "",
      airportType: "",
      positioning: "",
      ow: "",
      localCode: "",
      isoCountry: "",
      isoRegion: "",
      id: "",
      keywords: "",
      fowRegions: [],
      fowDisplayName: "",
      regions: [],
      alternateAirports25Mile: [],
      alternateAirports50Mile: [],
      discount25Mile: 0,
      discount50Mile: 0,
    },
    loading: true,
  };

  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [state, dispatch] = useReducer(reducer, initialState);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    let formData = { ...state.airport };

    // Remove deprecated region field (we use regions array now)
    delete formData.region;

    // Clean up alternate airports data - remove display labels
    if (formData.alternateAirports25Mile) {
      formData.alternateAirports25Mile = formData.alternateAirports25Mile.map(
        (airport) => ({
          airportId: airport.airportId,
          airportCode: airport.airportCode,
          airportLocalCode: airport.airportLocalCode,
          distance: airport.distance,
        })
      );
    }

    if (formData.alternateAirports50Mile) {
      formData.alternateAirports50Mile = formData.alternateAirports50Mile.map(
        (airport) => ({
          airportId: airport.airportId,
          airportCode: airport.airportCode,
          airportLocalCode: airport.airportLocalCode,
          distance: airport.distance,
        })
      );
    }

    // Remove any undefined or null values to prevent validation errors
    Object.keys(formData).forEach((key) => {
      if (formData[key] === undefined || formData[key] === null) {
        delete formData[key];
      }
    });

    await ai.auth(token).post(`/api/airports/create`, formData);

    history.push({ pathname: "/airports/" });

    setTimeout(() => {
      toast.success("Airport Created Successfully!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }, 50);
  };

  useEffect(() => {
    document.title = "New Airport | evoJets HQ";
  }, []);

  return (
    <Fragment>
      <h2 id="addAirportHeading">New Airport</h2>
      <form id="addAirport" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="code">Code</label>
          <input
            type="text"
            name="code"
            value={state.airport.code}
            placeholder="Code"
            onChange={(e) =>
              dispatch({
                type: "fieldChanged",
                field: e.target.name,
                value: e.target.value,
              })
            }
          ></input>
        </div>

        <div className="form-group">
          <label htmlFor="airportName">Airport Name</label>

          <input
            type="text"
            name="airportName"
            value={state.airport.airportName}
            placeholder="Airport Name"
            onChange={(e) =>
              dispatch({
                type: "fieldChanged",
                field: e.target.name,
                value: e.target.value,
              })
            }
          ></input>
        </div>

        <div className="form-group">
          <label htmlFor="oldId">Airport Old ID</label>

          <input
            type="text"
            name="oldId"
            value={state.airport.id}
            placeholder="Airport Old ID"
            onChange={(e) =>
              dispatch({
                type: "fieldChanged",
                field: "id",
                value: e.target.value,
              })
            }
          ></input>
        </div>

        <div className="form-group">
          <label htmlFor="airportActualName">Airport Actual Name</label>

          <input
            type="text"
            name="airportActualName"
            value={state.airport.airportActualName}
            placeholder="Airport Actual Name"
            onChange={(e) =>
              dispatch({
                type: "fieldChanged",
                field: e.target.name,
                value: e.target.value,
              })
            }
          ></input>
        </div>

        <div className="form-group">
          <label htmlFor="city">City</label>
          <input
            type="text"
            name="city"
            value={state.airport.city}
            placeholder="City"
            onChange={(e) =>
              dispatch({
                type: "fieldChanged",
                field: e.target.name,
                value: e.target.value,
              })
            }
          ></input>
        </div>

        <div className="form-group">
          <label htmlFor="lat">Latitude</label>
          <input
            type="number"
            name="lat"
            value={state.airport.lat}
            placeholder="Latitude"
            onChange={(e) =>
              dispatch({
                type: "fieldChanged",
                field: e.target.name,
                value: e.target.value,
              })
            }
          ></input>
        </div>

        <div className="form-group">
          <label htmlFor="lon">Longitude</label>
          <input
            type="number"
            name="lon"
            value={state.airport.lon}
            placeholder="Longitude"
            onChange={(e) =>
              dispatch({
                type: "fieldChanged",
                field: e.target.name,
                value: e.target.value,
              })
            }
          ></input>
        </div>

        <div className="form-group">
          <label htmlFor="municipality">Municipality</label>
          <input
            type="text"
            name="municipality"
            value={state.airport.municipality}
            placeholder="Municipality"
            onChange={(e) =>
              dispatch({
                type: "fieldChanged",
                field: e.target.name,
                value: e.target.value,
              })
            }
          ></input>
        </div>

        <div className="form-group">
          <label htmlFor="airportType">Airport Type</label>
          <input
            type="text"
            name="airportType"
            value={state.airport.airportType}
            placeholder="Airport Type"
            onChange={(e) =>
              dispatch({
                type: "fieldChanged",
                field: e.target.name,
                value: e.target.value,
              })
            }
          ></input>
        </div>

        <div className="form-group">
          <label htmlFor="positioning">Positioning</label>
          <input
            type="number"
            name="positioning"
            value={state.airport.positioning}
            placeholder="Positioning"
            onChange={(e) =>
              dispatch({
                type: "fieldChanged",
                field: e.target.name,
                value: e.target.value,
              })
            }
          ></input>
        </div>

        <div className="form-group">
          <label htmlFor="ow">One Way Ratio</label>
          <input
            type="number"
            name="ow"
            value={state.airport.ow}
            placeholder="One Way Ratio"
            onChange={(e) =>
              dispatch({
                type: "fieldChanged",
                field: e.target.name,
                value: e.target.value,
              })
            }
          ></input>
        </div>

        <div className="form-group">
          <label htmlFor="localCode">Local Code</label>
          <input
            type="text"
            name="localCode"
            value={state.airport.localCode}
            placeholder="Local Code"
            onChange={(e) =>
              dispatch({
                type: "fieldChanged",
                field: e.target.name,
                value: e.target.value,
              })
            }
          ></input>
        </div>

        <div className="form-group">
          <label htmlFor="keywords">Search Keywords</label>
          <input
            type="text"
            name="keywords"
            value={state.airport.keywords || ""}
            placeholder="Extra search terms, space-separated (e.g. legacy codes: KPBI PBI)"
            onChange={(e) =>
              dispatch({
                type: "fieldChanged",
                field: e.target.name,
                value: e.target.value,
              })
            }
          ></input>
        </div>

        <div className="form-group">
          <label htmlFor="isoCountry">ISO Country</label>

          <input
            type="text"
            name="isoCountry"
            value={state.airport.isoCountry}
            placeholder="ISO Country"
            onChange={(e) =>
              dispatch({
                type: "fieldChanged",
                field: e.target.name,
                value: e.target.value,
              })
            }
          ></input>
        </div>
        <div className="form-group">
          <label htmlFor="isoRegion">ISO Region</label>

          <input
            type="text"
            name="isoRegion"
            value={state.airport.isoRegion}
            placeholder="ISO Region"
            onChange={(e) =>
              dispatch({
                type: "fieldChanged",
                field: e.target.name,
                value: e.target.value,
              })
            }
          ></input>
        </div>

        <div className="form-group">
          <label htmlFor="region">Region</label>

          <input
            type="text"
            name="region"
            value={state.airport.region}
            placeholder="Region"
            onChange={(e) =>
              dispatch({
                type: "fieldChanged",
                field: e.target.name,
                value: e.target.value,
              })
            }
          />
        </div>

        <div className="form-group">
          <label htmlFor="subRegion">Sub-region</label>

          <input
            type="text"
            name="subRegion"
            value={state.airport.subRegion}
            placeholder="Sub-region"
            onChange={(e) =>
              dispatch({
                type: "fieldChanged",
                field: e.target.name,
                value: e.target.value,
              })
            }
          />
        </div>

        <RegionAutocomplete
          selectedRegion={
            state.airport.regions && state.airport.regions.length > 0
              ? state.airport.regions.map((region) => ({
                  value: region._id || region,
                  label: region.name || region,
                }))
              : null
          }
          handleRegionChange={(selectedOptions) => {
            const regionIds = selectedOptions
              ? selectedOptions.map((option) => option.value)
              : [];
            dispatch({
              type: "regionsChanged",
              value: regionIds,
            });
          }}
          isMulti={true}
          label="Regions"
          placeholder="Select regions for this airport"
        />

        <AlternateAirportMultiSelect
          currentAirportId={null}
          selectedAirports={state.airport.alternateAirports25Mile || []}
          handleChange={(alternates) => {
            dispatch({
              type: "alternateAirports25Changed",
              value: alternates,
            });
          }}
          label="Alternate Airports (25 Mile)"
          placeholder="Save airport first to add alternates"
        />

        <div className="form-group">
          <label htmlFor="discount25Mile">
            Discount % for 25 Mile Alternates
          </label>
          <input
            type="number"
            name="discount25Mile"
            value={state.airport.discount25Mile || 0}
            placeholder="0"
            step="0.1"
            min="0"
            max="100"
            onChange={(e) =>
              dispatch({
                type: "fieldChanged",
                field: e.target.name,
                value: parseFloat(e.target.value) || 0,
              })
            }
          ></input>
        </div>

        <AlternateAirportMultiSelect
          currentAirportId={null}
          selectedAirports={state.airport.alternateAirports50Mile || []}
          handleChange={(alternates) => {
            dispatch({
              type: "alternateAirports50Changed",
              value: alternates,
            });
          }}
          label="Alternate Airports (50 Mile)"
          placeholder="Save airport first to add alternates"
        />

        <div className="form-group">
          <label htmlFor="discount50Mile">
            Discount % for 50 Mile Alternates
          </label>
          <input
            type="number"
            name="discount50Mile"
            value={state.airport.discount50Mile || 0}
            placeholder="0"
            step="0.1"
            min="0"
            max="100"
            onChange={(e) =>
              dispatch({
                type: "fieldChanged",
                field: e.target.name,
                value: parseFloat(e.target.value) || 0,
              })
            }
          ></input>
        </div>

        <FOWRegionAutocomplete
          label="FOW - Regions"
          placeholder="Region(s) for FOWs"
          value={state.airport.fowRegions.map(region => ({
            value: region,
            label: region,
          }))}
          handleChange={(regions) => {
            dispatch({
              type: "fieldChanged",
              field: "fowRegions",
              value: (regions || []).map(({ value }) => value),
            });
          }}
        />

        <div className="form-group">
          <label htmlFor="fowDisplayName">FOW - Display Name</label>

          <input
            type="text"
            name="fowDisplayName"
            value={state.airport.fowDisplayName}
            placeholder="Display Name for FOWs"
            onChange={(e) =>
              dispatch({
                type: "fieldChanged",
                field: e.target.name,
                value: e.target.value,
              })
            }
          />
        </div>

        <input type="submit" value="Create Airport"></input>
      </form>
    </Fragment>
  );
};

export default NewAirportContent;
